
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback } from 'react';
import { motion, cubicBezier } from 'framer-motion';
import BookingForm from '../components/BookingForm';
import { ClockIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { useSEO } from '../hooks/useSEO';
import { trackEvent } from '../services/analyticsService';
import { submitBooking } from '../../Shared/services/mockBackend';
import { BookingDetails, AppPage } from '../types';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import Footer from '../components/Footer';

interface BookingPageProps {
  initialNotes?: string;
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const BookingPage: React.FC<BookingPageProps> = ({ initialNotes, onNavigate }) => {
  const { t } = useTranslation();

  useSEO(
    t('bookingPage_seo_title'),
    t('bookingPage_seo_desc'),
    t('bookingPage_seo_keywords').split(',').map(k => k.trim())
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New state for confirmation flow
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [pendingBooking, setPendingBooking] = useState<BookingDetails | null>(null);

  const handleBookingSubmit = useCallback(async (details: BookingDetails) => {
    // Instead of submitting, open confirmation modal
    setPendingBooking(details);
    setIsConfirmationOpen(true);
  }, []);

  const handleConfirmBooking = useCallback(async () => {
    if (!pendingBooking) return;

    setIsLoading(true);
    setError(null);
    setIsConfirmationOpen(false); // Close modal while processing

    try {
      await submitBooking(pendingBooking);
      trackEvent('booking_complete', {
        service_id: pendingBooking.serviceIds[0],
        date: pendingBooking.date,
        time: pendingBooking.time
      });
      setIsSuccess(true);
      setPendingBooking(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bookingForm_error_submit'));
    } finally {
      setIsLoading(false);
    }
  }, [pendingBooking, t]);

  const resetForm = useCallback(() => {
    setIsSuccess(false);
    setError(null);
    setPendingBooking(null);
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1) } },
  };

  return (
    <>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
        className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 overflow-y-auto custom-scrollbar"
      >
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 md:p-10 border border-gray-200 dark:border-gray-700 mb-8">
          <div className="flex flex-col items-center mb-8">
            <ClockIcon className="w-12 h-12 text-gray-700 dark:text-gray-200 mb-4" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white text-center leading-tight">
              {t('bookingPage_title')}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-3 text-center">
              {t('bookingPage_tagline')}
            </p>
          </div>

          <BookingForm
            onSubmit={handleBookingSubmit}
            isLoading={isLoading}
            isSuccess={isSuccess}
            error={error}
            resetForm={resetForm}
            initialNotes={initialNotes}
          />


          <div className="hidden"></div>
        </div>
        <Footer onNavigate={onNavigate} />
      </motion.div>
      <BookingConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={handleConfirmBooking}
        isLoading={isLoading}
        bookingDetails={pendingBooking}
      />
    </>
  );
};

export default BookingPage;
